import { useCallback, useEffect, useMemo, useState } from "react";
import useStationOptions from "../hooks/useStationOptions";
import stationLabel from "../utils/stationLabel";
import { isPurchasable } from "../services/departureRules";
import { getPastDepartureGraceMinutes, DEFAULT_PAST_DEPARTURE_GRACE_MIN } from "../services/runtimeConfig";
import {
    Container,
    Box,
    Button,
    Stack,
    IconButton,
    CircularProgress,
    TextField,
    MenuItem,
    Paper,
    Autocomplete,
    Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../auth/FirebaseContext";
import TripResultsList from "../components/TripResultsList";
import DateStrip from "../components/DateStrip";
import {
    setSelectedTrip,
    setDepartureStation,
    setArrivalStation,
    setJourneyDate,
    pickJourneyDate,
    setTripResults,
    setLoading,
    setError,
    setReturnDate,
    setTripType as setTripTypeAction,
    resetSearch,
} from "../redux/slices/searchSlice";
import { searchTripsApi, findNextDepartureDateApi } from "../api/trips";

export default function SearchResultsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { isAuthenticated } = useAuthContext();
    const {
        tripResults,
        isLoading,
        error,
        departureStation,
        arrivalStation,
        journeyDate,
        returnDate,
        tripType: reduxTripType,
    } = useSelector((state) => state.search);

    const [tripType, setTripType] = useState(reduxTripType || (returnDate ? "return" : "oneWay"));

    // Station search state
    // Stanice sa stvarnim polascima za izabrani datum; „Do" se sužava po polaznoj.
    const dep = useStationOptions(journeyDate);
    const arr = useStationOptions(journeyDate, departureStation?.id ?? null);

    // ESC = nazad i poništi rezultate
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                dispatch(resetSearch());
                navigate("/");
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [dispatch, navigate]);

    const handleSelectTrip = useCallback(
        (trip) => {
            dispatch(setSelectedTrip(trip));
            // Kupovina zahteva prijavu — gostu/neulogovanom otvaramo login prompt
            // na mestu (rezultati i izabran polazak ostaju), a ne šaljemo ga na
            // rezervaciju. Po prijavi ponovni „Kupi" vodi dalje.
            if (!isAuthenticated) {
                const params = new URLSearchParams(location.search);
                params.set("login", "1");
                navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
                return;
            }
            navigate("/rezervacija");
        },
        [dispatch, navigate, isAuthenticated, location.pathname, location.search]
    );

    const handleSearch = useCallback(
        async (newDate) => {
            const requested = newDate || journeyDate;
            if (!departureStation || !arrivalStation || !requested) return;

            // Clamp na današnji datum — pretraga ne sme da ide za prošli period
            // (npr. ako je stranica ostala otvorena preko ponoći).
            const today = dayjs().startOf("day");
            const dateToUse = dayjs(requested).isBefore(today, "day")
                ? today.format("YYYY-MM-DD")
                : requested;

            if (newDate) {
                // Izbor sa trake dana / pickera — korisnikova svesna odluka.
                dispatch(pickJourneyDate(dateToUse));
            } else if (dateToUse !== journeyDate) {
                dispatch(setJourneyDate(dateToUse));
            }
            dispatch(setLoading(true));
            dispatch(setError(null));
            try {
                const results = await searchTripsApi({
                    departureStationId: departureStation.id,
                    arrivalStationId: arrivalStation.id,
                    from: dateToUse,
                });
                dispatch(setTripResults(results || []));
            } catch (err) {
                console.error("Greška pri pretrazi:", err);
                dispatch(setError(t("homePage.searchError")));
                dispatch(setTripResults([]));
            } finally {
                dispatch(setLoading(false));
            }
        },
        [dispatch, departureStation, arrivalStation, journeyDate, t]
    );

    const handleDateChange = useCallback(
        (newDate) => {
            handleSearch(newDate);
        },
        [handleSearch]
    );

    // Luft za kupovinu posle predviđenog vremena na usputnoj stanici (runtime config)
    const [graceMinutes, setGraceMinutes] = useState(DEFAULT_PAST_DEPARTURE_GRACE_MIN);
    useEffect(() => {
        getPastDepartureGraceMinutes().then(setGraceMinutes);
    }, []);

    // Prazan rezultat ne sme da bude ćorsokak: relacija često vozi samo određenim
    // danima, pa potražimo prvi sledeći dan sa kupovinim polaskom i ponudimo skok.
    const [nextDate, setNextDate] = useState(null);
    useEffect(() => {
        setNextDate(null);
        if (isLoading || error) return undefined;
        if (!departureStation || !arrivalStation || !journeyDate) return undefined;
        if (tripResults && tripResults.length > 0) return undefined;
        let alive = true;
        findNextDepartureDateApi({
            departureStationId: departureStation.id,
            arrivalStationId: arrivalStation.id,
            after: journeyDate,
        })
            .then((d) => {
                if (alive) setNextDate(d);
            })
            .catch(() => {
                if (alive) setNextDate(null);
            });
        return () => {
            alive = false;
        };
    }, [tripResults, isLoading, error, departureStation, arrivalStation, journeyDate]);

    // Filteri
    const [sortBy, setSortBy] = useState("time_asc");
    const [selectedCarrier, setSelectedCarrier] = useState(null);
    const [timeRange, setTimeRange] = useState([0, 24]);
    const [directOnly, setDirectOnly] = useState(false);
    const [fastestOnly, setFastestOnly] = useState(false);

    const availableCarriers = useMemo(() => {
        if (!tripResults || tripResults.length === 0) return [];
        const map = new Map();
        tripResults.forEach((t) => {
            const name = t.operatorName || t.carrierName;
            const id = t.operatorId || t.carrierId;
            if (name && !map.has(name)) map.set(name, id);
        });
        return Array.from(map, ([name, id]) => ({ name, id }));
    }, [tripResults]);

    const handleSwapStations = useCallback(() => {
        const temp = departureStation;
        dispatch(setDepartureStation(arrivalStation));
        dispatch(setArrivalStation(temp));
    }, [dispatch, departureStation, arrivalStation]);

    const getTimeMinutes = useCallback((trip) => {
        const timeStr = trip.departureTime || trip.departureTimeStr || "";
        if (typeof timeStr === "string" && timeStr.match(/^\d{2}:\d{2}/)) {
            const [h, m] = timeStr.split(":").map(Number);
            return h * 60 + m;
        }
        try {
            const d = new Date(timeStr);
            if (!isNaN(d.getTime())) return d.getHours() * 60 + d.getMinutes();
        } catch {}
        return 0;
    }, []);

    const getTravelDuration = useCallback((trip) => {
        if (trip.timeTravelInMinutes) return trip.timeTravelInMinutes;
        const depStr = trip.departureTime || trip.departureTimeStr || "";
        const arrStr = trip.arrivalTime || trip.arrivalTimeStr || "";
        const parseMin = (str) => {
            if (typeof str === "string" && str.match(/^\d{2}:\d{2}/)) {
                const [h, m] = str.split(":").map(Number);
                return h * 60 + m;
            }
            try {
                const d = new Date(str);
                if (!isNaN(d.getTime())) return d.getHours() * 60 + d.getMinutes();
            } catch {}
            return null;
        };
        const dep = parseMin(depStr);
        const arr = parseMin(arrStr);
        if (dep === null || arr === null) return Infinity;
        let diff = arr - dep;
        if (diff <= 0) diff += 24 * 60;
        return diff;
    }, []);

    const filteredTrips = useMemo(() => {
        if (!tripResults || tripResults.length === 0) return [];

        const today = new Date();
        const todayStr = today.toISOString().split("T")[0];
        const isToday = journeyDate === todayStr;
        const isPast = journeyDate && journeyDate < todayStr;

        if (isPast) return [];

        let result = [...tripResults];

        // Polazak bez cene za traženi smer se ne može kupiti — ne prikazujemo ga
        // (npr. cenovnik važi samo za suprotan smer, ili povratna cena ne postoji).
        result = result.filter((t) =>
            tripType === "return"
                ? t.returnPrice != null
                : (t.price ?? t.basePrice) != null
        );

        // Prošli polasci za danas: sa početne stanice kupovina važi tačno do
        // vremena polaska, sa usputne još `graceMinutes` posle predviđenog
        // prolaska (kupac tada prihvata rizik da je autobus već prošao).
        if (isToday) {
            const nowMs = Date.now();
            result = result.filter((trip) => isPurchasable(trip, journeyDate, graceMinutes, nowMs));
        }

        // Filter po prevozniku
        if (selectedCarrier) {
            result = result.filter((t) => (t.operatorName || t.carrierName) === selectedCarrier.name);
        }

        // Filter po vremenu polaska
        if (timeRange[0] > 0 || timeRange[1] < 24) {
            result = result.filter((t) => {
                const mins = getTimeMinutes(t);
                const h = mins / 60;
                return h >= timeRange[0] && h <= timeRange[1];
            });
        }

        // Filter direktne linije
        if (directOnly) {
            result = result.filter((t) => !t.linkId);
        }

        // Filter najbrza
        if (fastestOnly && result.length > 0) {
            let minDuration = Infinity;
            let fastestTrip = result[0];
            result.forEach((t) => {
                const dur = getTravelDuration(t);
                if (dur < minDuration) {
                    minDuration = dur;
                    fastestTrip = t;
                }
            });
            result = [fastestTrip];
        }

        // Sortiranje
        if (sortBy === "time_asc") {
            result.sort((a, b) => getTimeMinutes(a) - getTimeMinutes(b));
        } else if (sortBy === "time_desc") {
            result.sort((a, b) => getTimeMinutes(b) - getTimeMinutes(a));
        } else if (sortBy === "price_asc") {
            result.sort((a, b) => (a.price ?? a.basePrice ?? 0) - (b.price ?? b.basePrice ?? 0));
        } else if (sortBy === "price_desc") {
            result.sort((a, b) => (b.price ?? b.basePrice ?? 0) - (a.price ?? a.basePrice ?? 0));
        }

        return result;
    }, [tripResults, journeyDate, tripType, graceMinutes, selectedCarrier, timeRange, directOnly, fastestOnly, sortBy, getTimeMinutes, getTravelDuration]);

    const activeFilterCount = [
        selectedCarrier !== null,
        timeRange[0] > 0 || timeRange[1] < 24,
        directOnly,
        fastestOnly,
    ].filter(Boolean).length;

    const resetFilters = () => {
        setSortBy("time_asc");
        setSelectedCarrier(null);
        setTimeRange([0, 24]);
        setDirectOnly(false);
        setFastestOnly(false);
    };

    return (
        <Box sx={{ minHeight: "calc(100vh - 64px)", bgcolor: "#f5f6fa" }}>
            {/* Search Bar */}
            <Box sx={{ bgcolor: "white", borderBottom: "1px solid", borderColor: "grey.200", py: 2.5 }}>
                <Container maxWidth="lg">
                    {/*<Typography variant="h4" sx={{ fontWeight: 800, mb: 2.5 }}>*/}
                    {/*    Autobuske karte*/}
                    {/*</Typography>*/}

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: { xs: "stretch", md: "flex-end" } }}>
                        {/* Od */}
                        <Autocomplete
                            sx={{ flex: { md: 1 }, width: { xs: "100%", md: "auto" }, minWidth: { md: 200 }, order: { xs: 1, md: 1 } }}
                            options={dep.options}
                            getOptionLabel={stationLabel}
                            value={departureStation}
                            onChange={(_, value) => dispatch(setDepartureStation(value))}
                            onInputChange={(_, value, reason) => {
                                if (reason === "input") dep.setInput(value);
                            }}
                            loading={dep.loading}
                            filterOptions={dep.serverFiltered ? (x) => x : undefined}
                            noOptionsText={dep.serverFiltered && dep.input.trim().length < 2 ? t("home.minTwoChars") : t("home.noStations")}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t("results.from")}
                                    placeholder={t("home.stationPlaceholder")}
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <Icon icon="mdi:map-marker-radius" width={18} color="#637381" style={{ marginRight: 4 }} />
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                        endAdornment: (
                                            <>
                                                {dep.loading ? <CircularProgress size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 1.5,
                                            bgcolor: "grey.50",
                                            height: 44,
                                        },
                                    }}
                                />
                            )}
                        />

                        {/* Swap stanica */}
                        <Tooltip title={t("results.swapStations")}>
                            <IconButton
                                onClick={() => { handleSwapStations(); }}
                                aria-label={t("common.swapStations")}
                                sx={{
                                    bgcolor: "primary.main",
                                    color: "white",
                                    borderRadius: 1.5,
                                    width: 44,
                                    height: 44,
                                    flexShrink: 0,
                                    order: { xs: 3, md: 2 },
                                    "&:hover": { bgcolor: "primary.dark", transform: "rotate(180deg)" },
                                    transition: "all 0.3s",
                                }}
                            >
                                <Icon icon="mdi:swap-vertical" width={20} />
                            </IconButton>
                        </Tooltip>

                        {/* Do */}
                        <Autocomplete
                            sx={{ flex: { md: 1 }, width: { xs: "100%", md: "auto" }, minWidth: { md: 200 }, order: { xs: 2, md: 3 } }}
                            options={arr.options}
                            getOptionLabel={stationLabel}
                            value={arrivalStation}
                            onChange={(_, value) => dispatch(setArrivalStation(value))}
                            onInputChange={(_, value, reason) => {
                                if (reason === "input") arr.setInput(value);
                            }}
                            loading={arr.loading}
                            filterOptions={arr.serverFiltered ? (x) => x : undefined}
                            noOptionsText={arr.serverFiltered && arr.input.trim().length < 2 ? t("home.minTwoChars") : t("home.noStations")}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t("results.to")}
                                    placeholder={t("home.stationPlaceholder")}
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <Icon icon="mdi:map-marker-radius" width={18} color="#637381" style={{ marginRight: 4 }} />
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                        endAdornment: (
                                            <>
                                                {arr.loading ? <CircularProgress size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 1.5,
                                            bgcolor: "grey.50",
                                            height: 44,
                                        },
                                    }}
                                />
                            )}
                        />

                        {/* Datum */}
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box sx={{ width: { xs: "auto", md: 150 }, flex: { xs: 1, md: "0 0 auto" }, minWidth: 0, order: { xs: 4, md: 4 } }}>
                                <DatePicker
                                    value={journeyDate ? dayjs(journeyDate) : null}
                                    onChange={(val) => {
                                        if (val) handleDateChange(val.format("YYYY-MM-DD"));
                                    }}
                                    format="DD.MM.YYYY"
                                    minDate={dayjs()}
                                    disablePast
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            label: t("results.departure"),
                                            fullWidth: true,
                                            sx: {
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 1.5,
                                                    bgcolor: "grey.50",
                                                    height: 44,
                                                },
                                            },
                                        },
                                    }}
                                />
                            </Box>
                        </LocalizationProvider>

                        {/* Pronadji */}
                        <Button
                            variant="contained"
                            onClick={() => handleSearch()}
                            endIcon={<Icon icon="mdi:magnify" width={20} />}
                            sx={{
                                height: 44,
                                px: 4,
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                borderRadius: 1.5,
                                bgcolor: "#1a1a2e",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                                order: { xs: 5, md: 5 },
                                width: { xs: "100%", md: "auto" },
                                "&:hover": { bgcolor: "#16213e" },
                            }}
                        >
                            {t("results.find")}
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* Date Strip */}
            <Container maxWidth="lg" sx={{ mt: 3 }}>
                {journeyDate && (
                    <DateStrip
                        selectedDate={journeyDate}
                        onDateChange={handleDateChange}
                    />
                )}
            </Container>

            {/* Filteri */}
            {!isLoading && tripResults && tripResults.length > 0 && (
                <Container maxWidth="lg" sx={{ mt: 2 }}>
                    <Paper sx={{ px: 2.5, py: 1.5, borderRadius: 2, border: "1px solid", borderColor: "grey.200" }}>
                        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
                            {/* 1. Tip karte */}
                            <TextField
                                select
                                size="small"
                                value={tripType}
                                onChange={(e) => {
                                    setTripType(e.target.value);
                                    dispatch(setTripTypeAction(e.target.value));
                                    if (e.target.value === "oneWay") dispatch(setReturnDate(null));
                                }}
                                label={t("results.ticketTypeFilter")}
                                sx={{ minWidth: 160, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                            >
                                <MenuItem value="oneWay">{t("home.oneWay")}</MenuItem>
                                <MenuItem value="return">{t("home.roundTrip")}</MenuItem>
                            </TextField>

                            {/* 2. Prevoznik */}
                            {availableCarriers.length > 1 && (
                                <Autocomplete
                                    size="small"
                                    options={availableCarriers}
                                    getOptionLabel={(o) => o.name || ""}
                                    value={selectedCarrier}
                                    onChange={(_, v) => setSelectedCarrier(v)}
                                    isOptionEqualToValue={(o, v) => o.name === v.name}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t("results.carrier")}
                                            placeholder={t("results.allCarriers")}
                                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                                        />
                                    )}
                                    sx={{ minWidth: 200, flex: 1 }}
                                />
                            )}

                            {/* 3. Sortiraj po */}
                            <TextField
                                select
                                size="small"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                label={t("results.sortBy")}
                                sx={{ minWidth: 170, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                            >
                                <MenuItem value="time_asc">{t("results.sortTimeAsc")}</MenuItem>
                                <MenuItem value="time_desc">{t("results.sortTimeDesc")}</MenuItem>
                                <MenuItem value="price_asc">{t("results.sortPriceAsc")}</MenuItem>
                                <MenuItem value="price_desc">{t("results.sortPriceDesc")}</MenuItem>
                            </TextField>

                            {/* 4. Vreme polaska */}
                            <TextField
                                select
                                size="small"
                                value={`${timeRange[0]}-${timeRange[1]}`}
                                onChange={(e) => {
                                    const [from, to] = e.target.value.split("-").map(Number);
                                    setTimeRange([from, to]);
                                }}
                                label={t("results.departureTimeFilter")}
                                sx={{ minWidth: 170, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                            >
                                <MenuItem value="0-24">{t("results.anyTime")}</MenuItem>
                                <MenuItem value="0-6">00:00 - 06:00</MenuItem>
                                <MenuItem value="6-10">06:00 - 10:00</MenuItem>
                                <MenuItem value="10-14">10:00 - 14:00</MenuItem>
                                <MenuItem value="14-18">14:00 - 18:00</MenuItem>
                                <MenuItem value="18-24">18:00 - 00:00</MenuItem>
                            </TextField>

                            {/* 5. Najbrza */}
                            <Button
                                variant={fastestOnly ? "contained" : "outlined"}
                                size="small"
                                onClick={() => setFastestOnly(!fastestOnly)}
                                startIcon={<Icon icon={fastestOnly ? "mdi:check" : "mdi:lightning-bolt"} width={16} />}
                                sx={{
                                    height: 40,
                                    minWidth: 120,
                                    borderRadius: 1.5,
                                    fontWeight: 600,
                                    textTransform: "none",
                                    borderColor: fastestOnly ? "primary.main" : "grey.300",
                                    color: fastestOnly ? "white" : "text.primary",
                                    "&:hover": { borderColor: "primary.main" },
                                }}
                            >
                                {t("results.fastest")}
                            </Button>

                            {/* 6. Direktna */}
                            <Button
                                variant={directOnly ? "contained" : "outlined"}
                                size="small"
                                onClick={() => setDirectOnly(!directOnly)}
                                startIcon={<Icon icon={directOnly ? "mdi:check" : "mdi:transit-connection-variant"} width={16} />}
                                sx={{
                                    height: 40,
                                    minWidth: 120,
                                    borderRadius: 1.5,
                                    fontWeight: 600,
                                    textTransform: "none",
                                    borderColor: directOnly ? "primary.main" : "grey.300",
                                    color: directOnly ? "white" : "text.primary",
                                    "&:hover": { borderColor: "primary.main" },
                                }}
                            >
                                {t("results.direct")}
                            </Button>

                            {/* Reset filtera */}
                            {activeFilterCount > 0 && (
                                <Button
                                    size="small"
                                    onClick={resetFilters}
                                    startIcon={<Icon icon="mdi:filter-remove" width={16} />}
                                    sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "none" }}
                                >
                                    {t("results.resetFilters")}
                                </Button>
                            )}
                        </Stack>
                    </Paper>
                </Container>
            )}

            {/* Results */}
            <Container maxWidth="lg" sx={{ mt: 2, pb: 6 }}>
                {isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TripResultsList
                        trips={filteredTrips}
                        isLoading={isLoading}
                        error={error}
                        onSelectTrip={handleSelectTrip}
                        tripType={tripType}
                        nextDate={(tripResults?.length ?? 0) === 0 ? nextDate : null}
                        onPickNextDate={handleDateChange}
                    />
                )}
            </Container>
        </Box>
    );
}
